import React, {useEffect, useState} from 'react'
import {Button, Form} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {useDebounce} from '../../../../../_metronic/helpers'
import {BACKEND_URL} from '../../../helpers/consts'

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

interface IUnderwritingQuestion {
  input_type: string
  type: string
  text: string
  currently_text?: string
  currently_checkbox?: boolean
  options?: string[]
  text2?: string
  type2?: string
}

interface IHistoryEntry {
  sessionId: string
  question: IUnderwritingQuestion | null
  hasNext: boolean
  hasPrev: boolean
  underwritingItems: any[]
}

interface IDateDraft {
  month: string
  day: string
  year: string
  currently: boolean
}

interface IDraft {
  date1: IDateDraft
  date2: IDateDraft
  radio: string
  yesNo: string
  text: string
}

interface IUnderwritingEntry {
  id: string
  name: string
  isDrug: boolean
  sessionId: string
  question: IUnderwritingQuestion | null
  hasNext: boolean
  hasPrev: boolean
  underwritingItems: any[]
  history: IHistoryEntry[]
  isComplete: boolean
  isLoading: boolean
  comboResult?: any
  draft: IDraft
}

interface IHealthUnderwritingSearchProps {
  toolkit: string
  dobYear: string
  onChange: (items: any[]) => void
}

const today = new Date()

const newDateDraft = (): IDateDraft => ({
  month: String(today.getMonth() + 1),
  day: String(today.getDate()),
  year: String(today.getFullYear()),
  currently: false,
})

const newDraft = (): IDraft => ({
  date1: newDateDraft(),
  date2: newDateDraft(),
  radio: '',
  yesNo: '',
  text: '',
})

const formatDateDraft = (d: IDateDraft) => `${d.year}-${d.month.padStart(2, '0')}-${d.day.padStart(2, '0')}`

const isAnswerReady = (question: IUnderwritingQuestion, draft: IDraft) => {
  switch (question.input_type) {
    case 'YES_NO':
      return !!draft.yesNo
    case 'RADIO':
      return !!draft.radio
    case 'DATE':
    case 'DOUBLE_DATE':
      return true
    default:
      return !!draft.text
  }
}

const buildAnswer = (question: IUnderwritingQuestion, draft: IDraft): any[] => {
  switch (question.input_type) {
    case 'YES_NO':
      return [draft.yesNo]
    case 'RADIO':
      return [draft.radio]
    case 'DATE':
      return [draft.date1.currently ? 'current' : formatDateDraft(draft.date1)]
    case 'DOUBLE_DATE':
      return [draft.date1.currently ? 'current' : formatDateDraft(draft.date1), formatDateDraft(draft.date2)]
    default:
      return [draft.text]
  }
}

export const HealthUnderwritingSearch: React.FC<IHealthUnderwritingSearchProps> = ({toolkit, dobYear, onChange}) => {
  const [conditionInput, setConditionInput] = useState('')
  const [medicationInput, setMedicationInput] = useState('')
  const [conditionSuggestions, setConditionSuggestions] = useState<string[]>([])
  const [medicationSuggestions, setMedicationSuggestions] = useState<string[]>([])
  const [items, setItems] = useState<IUnderwritingEntry[]>([])

  const conditionDebounced = useDebounce(conditionInput, 400)
  const medicationDebounced = useDebounce(medicationInput, 400)

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('TOKEN')}`,
  })

  const fetchSuggestions = async (name: string, isDrug: boolean) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/${isDrug ? 'drug-search' : 'condition-search'}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({name, toolkit}),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      const list = Array.isArray(data) ? data : []
      isDrug ? setMedicationSuggestions(list) : setConditionSuggestions(list)
    } catch {
      toast.error(`Error searching ${isDrug ? 'medications' : 'health conditions'}`)
    }
  }

  useEffect(() => {
    if (!conditionDebounced) {
      setConditionSuggestions([])
      return
    }
    fetchSuggestions(conditionDebounced, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionDebounced])

  useEffect(() => {
    if (!medicationDebounced) {
      setMedicationSuggestions([])
      return
    }
    fetchSuggestions(medicationDebounced, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicationDebounced])

  useEffect(() => {
    onChange(items.filter((i) => i.isComplete && i.comboResult).map((i) => i.comboResult))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  const updateItem = (id: string, patch: Partial<IUnderwritingEntry>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? {...i, ...patch} : i)))
  }

  const selectSuggestion = async (name: string, isDrug: boolean) => {
    isDrug ? setMedicationInput('') : setConditionInput('')
    isDrug ? setMedicationSuggestions([]) : setConditionSuggestions([])

    const sessionId = crypto.randomUUID()
    const id = sessionId

    setItems((prev) => [
      ...prev,
      {
        id,
        name,
        isDrug,
        sessionId,
        question: null,
        hasNext: true,
        hasPrev: false,
        underwritingItems: [],
        history: [],
        isComplete: false,
        isLoading: true,
        draft: newDraft(),
      },
    ])

    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/questionnaire-traversal`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({name, is_drug: isDrug, answer: [], session_id: sessionId, toolkit, action: 'init'}),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      updateItem(id, {
        sessionId: data.session_id || sessionId,
        question: data.question || null,
        hasNext: !!data.has_next,
        hasPrev: !!data.has_prev,
        underwritingItems: data.underwriting_items || [],
        isLoading: false,
      })
    } catch {
      toast.error('Error starting underwriting questionnaire')
      setItems((prev) => prev.filter((i) => i.id !== id))
    }
  }

  const buildComboItem = (item: IUnderwritingEntry, underwritingItems: any[], sessionId: string, hasNext: boolean, hasPrev: boolean) => ({
    answer: [],
    answers: underwritingItems,
    autoAdded: false,
    hasNext,
    hasPrev,
    hospitalization: false,
    hospitalizationReason: '',
    isDrug: item.isDrug,
    isHospitalizationReason: false,
    isLastQuestion: false,
    name: item.name,
    sessionId,
    type: item.isDrug ? 'Medication' : 'Health Condition',
    year: dobYear,
  })

  const completeItem = async (item: IUnderwritingEntry, underwritingItems: any[], sessionId: string, hasNext: boolean, hasPrev: boolean) => {
    const comboItem = buildComboItem(item, underwritingItems, sessionId, hasNext, hasPrev)
    try {
      await fetch(`${BACKEND_URL}/api/integrations/autofill-combo`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify([comboItem]),
      })
    } catch {
      // best-effort call, the combo item built above is used regardless of the response
    }
    updateItem(item.id, {isComplete: true, isLoading: false, comboResult: comboItem})
  }

  const submitAnswer = async (item: IUnderwritingEntry) => {
    if (!item.question) return
    const answer = buildAnswer(item.question, item.draft)

    const historyEntry: IHistoryEntry = {
      sessionId: item.sessionId,
      question: item.question,
      hasNext: item.hasNext,
      hasPrev: item.hasPrev,
      underwritingItems: item.underwritingItems,
    }
    updateItem(item.id, {isLoading: true})

    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/questionnaire-answer`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({action: 'answer', answer, is_drug: item.isDrug, name: item.name, session_id: item.sessionId, toolkit}),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      const sessionId = data.session_id || item.sessionId
      const underwritingItems = data.underwriting_items || []
      const hasNext = !!data.has_next
      const hasPrev = !!data.has_prev

      if (!hasNext) {
        updateItem(item.id, {
          sessionId,
          question: data.question || null,
          hasNext,
          hasPrev,
          underwritingItems,
          history: [...item.history, historyEntry],
        })
        await completeItem(item, underwritingItems, sessionId, hasNext, hasPrev)
      } else {
        updateItem(item.id, {
          sessionId,
          question: data.question || null,
          hasNext,
          hasPrev,
          underwritingItems,
          history: [...item.history, historyEntry],
          isLoading: false,
          draft: newDraft(),
        })
      }
    } catch {
      toast.error('Error submitting underwriting answer')
      updateItem(item.id, {isLoading: false})
    }
  }

  const goBack = (item: IUnderwritingEntry) => {
    if (item.history.length === 0) return
    const prevHistory = [...item.history]
    const last = prevHistory.pop() as IHistoryEntry
    updateItem(item.id, {
      sessionId: last.sessionId,
      question: last.question,
      hasNext: last.hasNext,
      hasPrev: last.hasPrev,
      underwritingItems: last.underwritingItems,
      history: prevHistory,
      isComplete: false,
      comboResult: undefined,
      draft: newDraft(),
    })
  }

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
  }

  const getResolvedDate = (item: IUnderwritingEntry) => {
    const dateEntry = item.underwritingItems.find((u) => u.type === 'time_dependent')
    return dateEntry?.answer as string | undefined
  }

  const renderDateGroup = (item: IUnderwritingEntry, draftKey: 'date1' | 'date2', showCurrently: boolean, question: IUnderwritingQuestion) => {
    const dateDraft = item.draft[draftKey]
    const setDateDraft = (patch: Partial<IDateDraft>) => updateItem(item.id, {draft: {...item.draft, [draftKey]: {...dateDraft, ...patch}}})
    return (
      <>
        <div className='d-flex gap-2 justify-content-center mb-2'>
          <Form.Select
            size='sm'
            style={{width: '90px'}}
            disabled={dateDraft.currently}
            value={dateDraft.month}
            onChange={(e) => setDateDraft({month: e.target.value})}
          >
            {MONTH_NAMES.map((m, idx) => <option key={m} value={idx + 1}>{m}</option>)}
          </Form.Select>
          <Form.Select
            size='sm'
            style={{width: '80px'}}
            disabled={dateDraft.currently}
            value={dateDraft.day}
            onChange={(e) => setDateDraft({day: e.target.value})}
          >
            {Array.from({length: 31}, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
          </Form.Select>
          <Form.Select
            size='sm'
            style={{width: '90px'}}
            disabled={dateDraft.currently}
            value={dateDraft.year}
            onChange={(e) => setDateDraft({year: e.target.value})}
          >
            {Array.from({length: 100}, (_, i) => today.getFullYear() - i).map((y) => <option key={y} value={y}>{y}</option>)}
          </Form.Select>
        </div>
        {showCurrently && question.currently_checkbox && (
          <Form.Check
            className='mb-2 text-center'
            type='checkbox'
            label={question.currently_text || 'Currently'}
            checked={dateDraft.currently}
            onChange={(e) => setDateDraft({currently: e.target.checked})}
          />
        )}
      </>
    )
  }

  return (
    <div>
      <Form.Group className='mb-2'>
        <div className='position-relative'>
          <Form.Control
            type='text'
            placeholder='Enter Health Condition / Personal History'
            value={conditionInput}
            onChange={(e) => setConditionInput(e.target.value)}
          />
          {conditionSuggestions.length > 0 && (
            <div className='list-group position-absolute w-100 shadow-sm' style={{zIndex: 10, maxHeight: '220px', overflowY: 'auto'}}>
              {conditionSuggestions.map((name) => (
                <button
                  key={name}
                  type='button'
                  className='list-group-item list-group-item-action'
                  onClick={() => selectSuggestion(name, false)}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
      </Form.Group>

      <Form.Group className='mb-3'>
        <div className='position-relative'>
          <Form.Control
            type='text'
            placeholder='Enter Medication'
            value={medicationInput}
            onChange={(e) => setMedicationInput(e.target.value)}
          />
          {medicationSuggestions.length > 0 && (
            <div className='list-group position-absolute w-100 shadow-sm' style={{zIndex: 10, maxHeight: '220px', overflowY: 'auto'}}>
              {medicationSuggestions.map((name) => (
                <button
                  key={name}
                  type='button'
                  className='list-group-item list-group-item-action'
                  onClick={() => selectSuggestion(name, true)}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
      </Form.Group>

      {items.map((item) => (
        <div key={item.id} className='border rounded p-2 mb-2'>
          {item.isComplete ? (
            <div className='d-flex align-items-center justify-content-between'>
              <div className='d-flex align-items-center gap-2'>
                <i className={`bi ${item.isDrug ? 'bi-capsule' : 'bi-heart-pulse'} text-primary`} />
                <span>{item.name}</span>
                {getResolvedDate(item) && <span className='text-muted small'>{getResolvedDate(item)}</span>}
              </div>
              <div className='d-flex align-items-center gap-2'>
                <Button variant='dark' size='sm' onClick={() => goBack(item)} title='Edit'>
                  <i className='bi bi-pencil' />
                </Button>
                <Button variant='link' className='p-0 text-dark' onClick={() => removeItem(item.id)} title='Remove'>
                  <i className='bi bi-x-lg' />
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className='d-flex align-items-center justify-content-between mb-2'>
                <div className='d-flex align-items-center gap-2'>
                  <i className={`bi ${item.isDrug ? 'bi-capsule' : 'bi-heart-pulse'} text-primary`} />
                  <span>{item.name}</span>
                </div>
                <Button variant='link' className='p-0 text-dark' onClick={() => removeItem(item.id)} title='Remove'>
                  <i className='bi bi-x-lg' />
                </Button>
              </div>

              {item.question && (
                <div className='ps-1'>
                  <div className='fw-semibold small mb-2 text-center'>{item.question.text}</div>

                  {item.question.input_type === 'DATE' && renderDateGroup(item, 'date1', true, item.question)}

                  {item.question.input_type === 'DOUBLE_DATE' && (
                    <>
                      {renderDateGroup(item, 'date1', true, item.question)}
                      <div className='fw-semibold small mb-2 text-center'>{item.question.text2}</div>
                      {renderDateGroup(item, 'date2', false, item.question)}
                    </>
                  )}

                  {item.question.input_type === 'RADIO' && (
                    <div className='mb-2 d-flex flex-column align-items-center'>
                      {(item.question.options || []).map((opt) => (
                        <Form.Check
                          key={opt}
                          type='radio'
                          name={`radio-${item.id}`}
                          label={opt}
                          checked={item.draft.radio === opt}
                          onChange={() => updateItem(item.id, {draft: {...item.draft, radio: opt}})}
                        />
                      ))}
                    </div>
                  )}

                  {item.question.input_type === 'YES_NO' && (
                    <div className='d-flex justify-content-center gap-2 mb-2'>
                      <Button
                        variant={item.draft.yesNo === 'Yes' ? 'dark' : 'outline-dark'}
                        size='sm'
                        onClick={() => updateItem(item.id, {draft: {...item.draft, yesNo: 'Yes'}})}
                      >
                        Yes
                      </Button>
                      <Button
                        variant={item.draft.yesNo === 'No' ? 'dark' : 'outline-dark'}
                        size='sm'
                        onClick={() => updateItem(item.id, {draft: {...item.draft, yesNo: 'No'}})}
                      >
                        No
                      </Button>
                    </div>
                  )}

                  {!['DATE', 'DOUBLE_DATE', 'RADIO', 'YES_NO'].includes(item.question.input_type) && (
                    <div className='d-flex justify-content-center mb-2'>
                      <Form.Control
                        size='sm'
                        style={{maxWidth: '220px'}}
                        value={item.draft.text}
                        onChange={(e) => updateItem(item.id, {draft: {...item.draft, text: e.target.value}})}
                      />
                    </div>
                  )}

                  <div className='d-flex justify-content-center gap-2'>
                    {item.history.length > 0 && (
                      <Button variant='light' size='sm' disabled={item.isLoading} onClick={() => goBack(item)}>Back</Button>
                    )}
                    <Button
                      variant='dark'
                      size='sm'
                      disabled={item.isLoading || !isAnswerReady(item.question, item.draft)}
                      onClick={() => submitAnswer(item)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
